import React from 'react';
import { flushSync } from 'react-dom';
import type { Settings as LayoutSettings } from '@ant-design/pro-layout';
import { PageLoading } from '@ant-design/pro-layout';
import { notification, ConfigProvider, theme as antdTheme } from 'antd';
import type { RequestConfig, RunTimeLayoutConfig } from 'umi';
import { history } from 'umi';
import RightContent from '@/components/RightContent';
import Footer from '@/components/Footer';
import type { ResponseError } from 'umi-request';
import { queryCurrent as queryCurrentUser } from './services/user';
import { BookOutlined, LinkOutlined } from '@ant-design/icons';
import 'antd/dist/reset.css';
import enUS from 'antd/lib/locale/en_US';
import {
  applyThemeMode,
  getStoredThemeMode,
  THEME_CHANGE_EVENT,
  ThemeMode,
} from '@/utils/themeMode';

if (enUS) {
  const target = (enUS as any).default || enUS;
  if (target.Pagination) {
    target.Pagination.items_per_page = '/ Page';
  }
}

/** 获取用户信息比较慢的时候会展示一个 loading */
export const initialStateConfig = {
  loading: <PageLoading />,
};

const ThemeRoot: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = React.useState<ThemeMode>(() => getStoredThemeMode());

  React.useLayoutEffect(() => {
    applyThemeMode(mode);
  }, [mode]);

  React.useEffect(() => {
    const onThemeChange = (event: Event) => {
      const nextMode = (event as CustomEvent<ThemeMode>).detail;
      if (nextMode === 'light' || nextMode === 'dark') {
        document.documentElement.classList.add('theme-switching');
        document.body.classList.add('theme-switching');
        applyThemeMode(nextMode);
        flushSync(() => setMode(nextMode));
        window.requestAnimationFrame(() => {
          document.documentElement.classList.remove('theme-switching');
          document.body.classList.remove('theme-switching');
        });
      }
    };
    window.addEventListener(THEME_CHANGE_EVENT, onThemeChange);
    return () => window.removeEventListener(THEME_CHANGE_EVENT, onThemeChange);
  }, []);

  const isDark = mode === 'dark';

  return (
    <ConfigProvider
      locale={enUS}
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#3487e5',
          fontSize: 13,
          colorBgLayout: isDark ? '#0f172a' : '#f0f2f5',
          colorBgContainer: isDark ? '#111827' : '#ffffff',
          colorBgElevated: isDark ? '#111827' : '#ffffff',
          colorText: isDark ? 'rgba(255, 255, 255, 0.88)' : 'rgba(0, 0, 0, 0.88)',
          colorBorder: isDark ? '#273449' : '#d9d9d9',
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
};

// Ant Design theme configuration
export function rootContainer(container: React.ReactNode) {
  return <ThemeRoot>{container}</ThemeRoot>;
}

export interface InitialStateInterface {
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<InitialStateInterface> {
  const fetchUserInfo = async () => {
    try {
      const currentUser = await queryCurrentUser();
      localStorage.setItem('currentUser', JSON.stringify(currentUser));
      return currentUser;
    } catch (error) {
      history.push('/user/login');
    }
    return undefined;
  };

  // 如果是登录页面，不执行
  if (history.location.pathname !== '/user/login') {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: {},
    };
  }
  return {
    fetchUserInfo,
    settings: {},
  };
}

// https://umijs.org/zh-CN/plugins/plugin-layout
export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  return {
    rightContentRender: () => <RightContent />,
    actionsRender: () => [],
    disableContentMargin: false,
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== '/user/login') {
        history.push('/user/login');
      }
    },
    links: [
      <>
        <LinkOutlined />
        <span
          onClick={() => {
            window.open('/umi/plugin/openapi');
          }}
        >
          openAPI
        </span>
      </>,
      <>
        <BookOutlined />
        <span
          onClick={() => {
            window.open('/~docs');
          }}
        >
          业务组件文档
        </span>
      </>,
    ],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    ...initialState?.settings,
  };
};

const errorHandler = (error: ResponseError) => {
  const { response, data } = error as any;
  if (response && response.status) {
    const { status, url } = response;
    const cleanUrl = url ? url.split('?')[0] : '';
    const path = cleanUrl.replace(/^https?:\/\/[^\/]+/, '');

    // umi-request attaches the parsed response body as error.data
    const body = data;
    const rawMessage =
      body?.message ||
      body?.body?.message ||
      body?.data?.message ||
      body?.errorString ||
      body?.body?.errorString ||
      body?.data?.errorString ||
      body?.error ||
      body?.body?.error ||
      body?.data?.error ||
      body?.msg ||
      body?.body?.msg ||
      body?.data?.msg ||
      body?.detail ||
      body?.body?.detail ||
      body?.data?.detail ||
      null;

    const backendMessage = typeof rawMessage === 'string' ? rawMessage : rawMessage ? JSON.stringify(rawMessage) : null;

    if (backendMessage) {
      notification.error({
        message: (
          <div style={{ fontSize: '12px', color: '#3d3c3cff', fontWeight: 'normal', wordBreak: 'break-all', lineHeight: '1.4', paddingRight: '12px' }}>
            Error {status}: {path}
          </div>
        ),
        description: (
          <div style={{ fontSize: '14px', fontWeight: 600, lineHeight: '1.4', paddingRight: '12px', marginTop: '4px' }}>
            {backendMessage}
          </div>
        ),
      });
    } else {
      notification.error({
        message: (
          <div style={{ fontSize: '13px', fontWeight: 'normal', wordBreak: 'break-all', lineHeight: '1.4', paddingRight: '12px' }}>
            Error {status}: {path}
          </div>
        ),
      });
    }
  }

  if (!response) {
    notification.error({
      message: 'Network Error',
      description: 'Unable to connect to the server.',
    });
  }
  throw error;
};

// https://umijs.org/zh-CN/plugins/plugin-request
export const request: RequestConfig = {
  errorHandler,
};